import { ATTENDANCE_STATUSES, type AttendanceStatus } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api/http';
import {
  Notice,
  SchoolSelect,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

export const todayIso = () => new Date().toLocaleDateString('en-CA');

interface SectionRow {
  sectionId: string;
  sectionName: string;
  className: string;
  classTeacher: string | null;
  studentCount: number;
  submitted: boolean;
  present: number;
  absent: number;
  late: number;
  excused: number;
  halfDay: number;
}
interface SummaryResponse {
  date: string;
  schoolDay: boolean;
  nonSchoolReason: string | null;
  sections: SectionRow[];
}

interface RegisterStudent {
  studentId: string;
  rollNumber: string | null;
  name: string;
  status: AttendanceStatus | null;
  remarks: string | null;
  onApprovedLeave: boolean;
}
interface Register {
  sectionName: string;
  className: string;
  date: string;
  schoolDay: boolean;
  nonSchoolReason: string | null;
  submitted: boolean;
  canEdit: boolean;
  students: RegisterStudent[];
}

type Draft = Record<string, { status: AttendanceStatus; remarks: string }>;

export function AttendancePage() {
  const [schoolId, setSchoolId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [sectionId, setSectionId] = useState('');

  const summary = useQuery({
    queryKey: ['attendance', 'summary', schoolId, date],
    queryFn: () =>
      api<SummaryResponse>(`/schools/${schoolId}/attendance/summary`, { query: { date } }),
    enabled: !!schoolId && !!date,
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('att.title')}</h1>
      <div className="flex flex-wrap items-end gap-4">
        <SchoolSelect
          value={schoolId}
          onChange={(id) => {
            setSchoolId(id);
            setSectionId('');
          }}
        />
        <TextField
          label={t('att.date')}
          type="date"
          value={date}
          max={todayIso()}
          onChange={(e) => {
            setDate(e.target.value);
            setSectionId('');
          }}
        />
      </div>

      {summary.isError && <Notice kind="error">{errorMessage(summary.error)}</Notice>}
      {summary.data?.nonSchoolReason && (
        <Notice kind="error">{summary.data.nonSchoolReason}</Notice>
      )}
      {summary.data && summary.data.sections.length === 0 && (
        <p className="text-slate-600">{t('att.noSections')}</p>
      )}
      {summary.data && summary.data.sections.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">{t('att.summary')}</caption>
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="p-3">
                  {t('att.section')}
                </th>
                <th scope="col" className="p-3">
                  {t('att.classTeacher')}
                </th>
                <th scope="col" className="p-3">
                  {t('att.students')}
                </th>
                <th scope="col" className="p-3">
                  {t('att.status.present')}
                </th>
                <th scope="col" className="p-3">
                  {t('att.status.absent')}
                </th>
                <th scope="col" className="p-3">
                  {t('att.state')}
                </th>
                <th scope="col" className="p-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.data.sections.map((row) => (
                <tr
                  key={row.sectionId}
                  className={row.sectionId === sectionId ? 'bg-brand-50' : ''}
                >
                  <th scope="row" className="p-3 font-medium">
                    {row.className} – {row.sectionName}
                  </th>
                  <td className="p-3">{row.classTeacher ?? '—'}</td>
                  <td className="p-3">{row.studentCount}</td>
                  <td className="p-3">{row.present + row.late}</td>
                  <td className="p-3">{row.absent}</td>
                  <td className="p-3">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${row.submitted ? 'bg-green-100 text-green-900' : 'bg-amber-100 text-amber-900'}`}
                    >
                      {row.submitted ? t('att.submitted') : t('att.notSubmitted')}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={`${t('att.open')}: ${row.className} – ${row.sectionName}`}
                      onClick={() => setSectionId(row.sectionId)}
                    >
                      {t('att.open')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {schoolId && sectionId ? (
        <RegisterPanel
          key={`${sectionId}-${date}`}
          schoolId={schoolId}
          sectionId={sectionId}
          date={date}
        />
      ) : (
        summary.data &&
        summary.data.sections.length > 0 && <p className="text-slate-600">{t('att.pickSection')}</p>
      )}
    </div>
  );
}

function RegisterPanel({
  schoolId,
  sectionId,
  date,
}: {
  schoolId: string;
  sectionId: string;
  date: string;
}) {
  const queryClient = useQueryClient();
  const url = `/schools/${schoolId}/sections/${sectionId}/attendance/${date}`;
  const [draft, setDraft] = useState<Draft>({});
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const register = useQuery({
    queryKey: ['attendance', 'register', url],
    queryFn: () => api<Register>(url),
  });
  const data = register.data;

  // Unmarked students default to Present, so a teacher only has to touch the exceptions.
  useEffect(() => {
    if (!data) return;
    setDraft(
      Object.fromEntries(
        data.students.map((s) => [
          s.studentId,
          { status: s.status ?? 'present', remarks: s.remarks ?? '' },
        ]),
      ),
    );
  }, [data]);

  const save = useMutation({
    mutationFn: (body: object) => api<Register>(url, { method: 'PUT', body }),
    onSuccess: (saved) => {
      setReason('');
      setMessage({ kind: 'ok', text: t('att.saved') });
      queryClient.setQueryData(['attendance', 'register', url], saved);
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'summary', schoolId] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  if (register.isPending) return <p role="status">{t('common.loading')}</p>;
  if (register.isError || !data)
    return <Notice kind="error">{errorMessage(register.error)}</Notice>;

  const editable = data.canEdit;
  const changed = data.students.filter((s) => {
    const d = draft[s.studentId];
    return d && (d.status !== s.status || (d.remarks || null) !== (s.remarks ?? null));
  });

  const submit = () => {
    setMessage(null);
    const rows = data.submitted ? changed : data.students;
    save.mutate({
      records: rows.map((s) => ({
        studentId: s.studentId,
        status: draft[s.studentId]?.status ?? 'present',
        remarks: draft[s.studentId]?.remarks || null,
      })),
      ...(data.submitted ? { reason } : {}),
    });
  };

  const setAll = (status: AttendanceStatus) =>
    setDraft(
      Object.fromEntries(
        data.students.map((s) => [
          s.studentId,
          { status, remarks: draft[s.studentId]?.remarks ?? '' },
        ]),
      ),
    );

  return (
    <section
      aria-label={t('att.registerFor', { section: `${data.className} – ${data.sectionName}` })}
      className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
    >
      <h2 className="text-lg font-semibold">
        {t('att.registerFor', { section: `${data.className} – ${data.sectionName}` })}
      </h2>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {!editable && <Notice kind="error">{data.nonSchoolReason ?? t('att.readOnly')}</Notice>}
      {data.students.length === 0 && <p className="text-slate-600">{t('att.noStudents')}</p>}
      {data.students.length > 0 && (
        <>
          {editable && (
            <button type="button" className={secondaryButton} onClick={() => setAll('present')}>
              {t('att.markAllPresent')}
            </button>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="p-2">
                    {t('att.roll')}
                  </th>
                  <th scope="col" className="p-2">
                    {t('att.student')}
                  </th>
                  <th scope="col" className="p-2">
                    {t('att.status')}
                  </th>
                  <th scope="col" className="p-2">
                    {t('att.remarks')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.students.map((s) => (
                  <tr key={s.studentId}>
                    <td className="p-2">{s.rollNumber ?? '—'}</td>
                    <th scope="row" className="p-2 font-medium">
                      {s.name}
                      {s.onApprovedLeave && (
                        <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-900">
                          {t('att.onLeave')}
                        </span>
                      )}
                    </th>
                    <td className="p-2">
                      <select
                        aria-label={t('att.statusFor', { name: s.name })}
                        disabled={!editable}
                        value={draft[s.studentId]?.status ?? s.status ?? 'present'}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            [s.studentId]: {
                              status: e.target.value as AttendanceStatus,
                              remarks: draft[s.studentId]?.remarks ?? '',
                            },
                          })
                        }
                        className={selectClass}
                      >
                        {ATTENDANCE_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {t(`att.status.${status}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <input
                        aria-label={t('att.remarksFor', { name: s.name })}
                        disabled={!editable}
                        maxLength={500}
                        value={draft[s.studentId]?.remarks ?? ''}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            [s.studentId]: {
                              status: draft[s.studentId]?.status ?? 'present',
                              remarks: e.target.value,
                            },
                          })
                        }
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editable && (
            <div className="flex flex-wrap items-end gap-3">
              {data.submitted && (
                <TextField
                  className="min-w-64 flex-1"
                  label={t('att.reason')}
                  value={reason}
                  maxLength={300}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
              <button
                type="button"
                className={primaryButton}
                disabled={
                  save.isPending || (data.submitted && (changed.length === 0 || !reason.trim()))
                }
                onClick={submit}
              >
                {data.submitted ? t('att.saveChanges') : t('att.submit')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
